import { useMemo } from "react";
import { Link } from "react-router-dom";
import { FCX } from "@datassential/platform-ui-lib";
import { getPercentInRange } from "@core/helpers";
import { routeNames } from "@core/routeNames";
import "./PeopleChart.scss";

export interface PeopleChartItem {
    id: number;
    name: string;
    value: number;
}

interface PeopleChartProps {
    data: PeopleChartItem[];
}

const BARS_GAP = 4;

const PeopleChart: FCX<PeopleChartProps> = ({ data }) => {
    const { minValue, valueRange, barWidth } = useMemo(() => {
        const width = data.length > 0
            ? `calc((100% - ${(data.length - 1) * BARS_GAP}px) / ${data.length})`
            : '100%';

        if (data.length === 0) {
            return { maxValue: 0, minValue: 0, valueRange: 0, barWidth: width };
        }

        const values = data.map(item => item.value || 0);
        const max = Math.max(...values);
        const min = Math.min(...values);
        const range = max - min;

        return {
            maxValue: max,
            minValue: min,
            valueRange: range,
            barWidth: width
        };
    }, [data]);

    return (
        <div className="PeopleChart">
            <div className="PeopleChart__label"></div>
            <div className="PeopleChart__bars" style={{ gap: BARS_GAP }}>
                {data.map((item, index) => {
                    const value = item.value || 0;
                    const heightPercent = valueRange > 0 ? getPercentInRange(value, minValue, minValue + valueRange) : 0;
                    const barHeight = Math.max(heightPercent, 3);
                    const encodedName = encodeURIComponent(item.name || '');

                    return (
                        <Link
                            key={`bar-${item.id || index}`}
                            className={`PeopleChart__bar appears-top del-${index+5} fast`}
                            style={{ width: barWidth, height: `${barHeight}%` }}
                            to={{
                                pathname: routeNames.food,
                                search: `?q=${encodedName}`
                            }}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <div className="PeopleChart__bar-value">
                                {value > 0 ? `${value}%` : `${value}%`}
                            </div>
                        </Link>
                    );
                })}
            </div>

            <div className="PeopleChart__names-row" style={{ gap: BARS_GAP }}>
                {data.map((item, index) => (
                    <div
                        key={`name-${item.id || index}`}
                        className={`PeopleChart__bar-name-wrapper appears-top del-${index + 5} fast`}
                        style={{ width: barWidth }}
                    >
                        <span className="PeopleChart__bar-name">{item.name}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PeopleChart;
